import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { educationAPI } from "../api/axiosClient";
import { ResetPasswordRequest } from "../api/types";
import { ArrowLeft, CheckCircle, Eye, EyeOff, Lock } from "lucide-react";

interface ResetPasswordFormProps {
  onBack: () => void;
  onSuccess: () => void;
  phoneCode: string;
  phoneNumber: string;
  otpCode: string;
  email?: string; // Email parametresi eklendi
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  onBack,
  onSuccess,
  phoneCode,
  phoneNumber,
  otpCode,
  email,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => {
    setError(null);
  };

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validatePassword = (password: string) => {
    const isNumeric = /^\d+$/.test(password);
    const isLength6 = password.length === 6;

    return {
      isValid: isNumeric && isLength6,
      isNumeric,
      isLength6,
    };
  };

  const passwordValidation = validatePassword(formData.newPassword);
  const passwordsMatch =
    formData.newPassword === formData.confirmPassword &&
    formData.confirmPassword.length > 0;

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.newPassword) {
      newErrors.newPassword = "Yeni şifre gereklidir";
    } else if (!passwordValidation.isValid) {
      if (!passwordValidation.isNumeric) {
        newErrors.newPassword = "Şifre sadece rakamlardan oluşmalıdır";
      } else if (!passwordValidation.isLength6) {
        newErrors.newPassword = "Şifre 6 haneli olmalıdır";
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Şifre tekrarı gereklidir";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Şifreler eşleşmiyor";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (error) {
      clearError();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      console.log("Reset password attempt:", {
        email,
        phoneCode,
        phoneNumber,
        otpCode,
        newPassword: formData.newPassword ? "***" : "undefined",
      });

      const request: ResetPasswordRequest = {
        phoneNumber: phoneNumber, // Sadece telefon numarası (5425019511)
        phoneCode: phoneCode, // Ülke kodu (90)
        otpCode: otpCode,
        newPassword: formData.newPassword,
      };

      setLoading(true);
      setError(null);
      const response = await educationAPI.resetPassword(request);

      if (response?.success) {
        setIsSubmitted(true);
        // Show success message for 3 seconds then go back to login
        setTimeout(() => {
          onSuccess();
        }, 3000);
      }
    } catch (err) {
      console.error("Reset password error:", err);
      setError(err instanceof Error ? err.message : "Reset password failed");
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex">
        <div className="hidden lg:flex lg:w-1/2 bg-[#9B51E0] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full"></div>
            <div className="absolute top-40 right-20 w-48 h-48 bg-white/5 rounded-full"></div>
            <div className="absolute bottom-32 left-20 w-40 h-40 bg-white/10 rounded-full"></div>
          </div>
          <div className="relative z-10 flex flex-col justify-center px-12 text-white">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Başarılı!</h1>
            <p className="text-lg text-white/80">Şifreniz güncellendi</p>
          </div>
        </div>
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
          <div className="w-full max-w-md text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Şifre Başarıyla Güncellendi!
            </h2>
            <p className="text-gray-500 mb-8">
              Yeni şifreniz ile giriş yapabilirsiniz.
            </p>
            <Card className="border-0 shadow-xl bg-white">
              <CardContent className="p-6">
                <p className="text-sm text-gray-600 mb-6">
                  Şifreniz başarıyla güncellendi. Artık yeni şifreniz ile giriş
                  yapabilirsiniz.
                </p>
                <Button
                  onClick={onSuccess}
                  className="w-full h-11 bg-[#9B51E0] hover:bg-[#8B41D0]"
                >
                  Giriş sayfasına dön
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Sol Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#9B51E0] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute top-40 right-20 w-48 h-48 bg-white/5 rounded-full"></div>
          <div className="absolute bottom-32 left-20 w-40 h-40 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-20 right-10 w-24 h-24 bg-white/5 rounded-full"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Yeni Şifre Belirle</h1>
            <p className="text-lg text-white/80">
              Hesabınız için güvenli bir şifre oluşturun
            </p>
          </div>

          <div className="space-y-4 mt-8">
            <div className="flex items-center space-x-3 text-white/80">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span>6 haneli numerik şifre</span>
            </div>
            <div className="flex items-center space-x-3 text-white/80">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span>Kolay hatırlanabilir</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sağ Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#9B51E0] rounded-2xl mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">
              Yeni Şifre Belirle
            </h2>
            <p className="text-gray-500 mt-2">Güvenli bir şifre belirleyin</p>
          </div>

          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label
                    htmlFor="newPassword"
                    className="text-sm font-medium text-gray-700"
                  >
                    Yeni Şifre
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type={showPassword ? "text" : "password"}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="••••••"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      required
                      className={`w-full pr-10 text-left text-lg font-mono ${
                        errors.newPassword
                          ? "border-red-300 focus:border-red-500"
                          : ""
                      }`}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      disabled={loading}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {errors.newPassword && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.newPassword}
                    </p>
                  )}

                  {/* Password Strength Indicator */}
                  {formData.newPassword && !errors.newPassword && (
                    <div className="space-y-2">
                      <div className="flex space-x-2">
                        <div
                          className={`w-1/2 h-2 rounded-full ${
                            passwordValidation.isNumeric
                              ? "bg-green-500"
                              : "bg-gray-200"
                          }`}
                        ></div>
                        <div
                          className={`w-1/2 h-2 rounded-full ${
                            passwordValidation.isLength6
                              ? "bg-green-500"
                              : "bg-gray-200"
                          }`}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <div
                          className={`flex items-center space-x-2 ${passwordValidation.isNumeric ? "text-green-600" : "text-gray-400"}`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${passwordValidation.isNumeric ? "bg-green-500" : "bg-gray-300"}`}
                          ></div>
                          <span>Sadece rakam</span>
                        </div>
                        <div
                          className={`flex items-center space-x-2 ${passwordValidation.isLength6 ? "text-green-600" : "text-gray-400"}`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${passwordValidation.isLength6 ? "bg-green-500" : "bg-gray-300"}`}
                          ></div>
                          <span>6 haneli</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="confirmPassword"
                    className="text-sm font-medium text-gray-700"
                  >
                    Şifre Tekrar
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="••••••"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      className={`w-full pr-10 text-left text-lg font-mono ${
                        errors.confirmPassword
                          ? "border-red-300 focus:border-red-500"
                          : ""
                      }`}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      disabled={loading}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {errors.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#9B51E0] hover:bg-[#8B41D0] text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-[#9B51E0]/25"
                  disabled={
                    loading ||
                    !passwordValidation.isValid ||
                    !passwordsMatch ||
                    formData.newPassword.length === 0 ||
                    formData.confirmPassword.length === 0
                  }
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Güncelleniyor...
                    </span>
                  ) : (
                    "Şifreyi Güncelle"
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={onBack}
                  className="flex items-center justify-center space-x-2 text-gray-500 hover:text-[#9B51E0] font-medium transition-colors mx-auto"
                  disabled={loading}
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Geri Dön</span>
                </button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-gray-400 mt-8">
            © 2026 EduPayz. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
